import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigLoaderService } from '../config-loader.service';

/**
 * Attaches the browser's IANA time-zone identifier (e.g. "America/Chicago")
 * to every outbound LBEMC API request via the X-Client-TimeZone header.
 *
 * The backend uses this header to interpret "today" and similar calendar
 * concepts in the user's local time rather than a fixed clinic zone. If the
 * header is missing or invalid, the backend falls back to America/Los_Angeles
 * (see Infrastructure/Utilities/RequestTimeZone on the API side).
 *
 * The header is only attached to requests targeting the configured API base
 * URL so the IANA zone (a fingerprintable piece of user data) does not leak
 * to third-party origins (e.g. analytics, S3 uploads, asset hosts).
 *
 * The zone is resolved once at construction and cached. Intl.DateTimeFormat
 * is built into modern browsers, so no third-party dependency is needed.
 */
@Injectable()
export class ClientTimeZoneInterceptor implements HttpInterceptor {
    private readonly timeZone: string;

    constructor(private configService: ConfigLoaderService) {
        let resolved = '';
        try {
            resolved = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        } catch {
            resolved = '';
        }
        this.timeZone = resolved;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.timeZone && this.isApiRequest(request.url)) {
            request = request.clone({
                setHeaders: {
                    'X-Client-TimeZone': this.timeZone
                }
            });
        }
        return next.handle(request);
    }

    // Only attach the header to requests targeting our API. ConfigLoaderService
    // populates apiUrl from appconfig.json at app bootstrap; if it's not ready
    // yet (or absent) we err on the side of not leaking the header.
    //
    // Match by parsed-URL origin + path prefix rather than a raw substring
    // compare. A naive indexOf check would also match
    // "https://api.example.com.attacker.com/" when apiUrl is
    // "https://api.example.com" because the attacker host has the apiUrl as
    // a string prefix. Parsing both URLs against the document origin handles
    // relative apiUrls (e.g. "/api" via the dev-server proxy) and absolute
    // apiUrls uniformly, and rejects substring-prefix lookalike hosts.
    private isApiRequest(url: string): boolean {
        const apiUrl = this.configService && this.configService.config && this.configService.config.apiUrl;
        if (!apiUrl) {
            return false;
        }
        try {
            const base = window.location.origin;
            const reqUrl = new URL(url, base);
            const apiUrlResolved = new URL(apiUrl, base);
            if (reqUrl.origin !== apiUrlResolved.origin) {
                return false;
            }
            const apiPath = apiUrlResolved.pathname.endsWith('/')
                ? apiUrlResolved.pathname
                : apiUrlResolved.pathname + '/';
            return reqUrl.pathname === apiUrlResolved.pathname
                || reqUrl.pathname.startsWith(apiPath);
        } catch {
            return false;
        }
    }
}
